import React from "react";
import { cn } from "./cn";
import { XIcon } from '@heroicons/react/outline';

/**
 * Bun badge/chip. Spec: Component Library "Chips & badges".
 *
 * tone: semantic pair — "info" | "success" | "warning" | "danger" | "special" | "neutral"
 * Answer scales also use "yellow", "orange", "orange-strong", "purple", "purple-strong".
 * Meeting types map onto tones (kept here so every screen agrees):
 *   Check-In=info, Planning=danger, Prospect=success, Trading=special, Onboarding=warning.
 * variant: "pill" (default, 999 radius) | "new" (ink chip, radius 6) | "count" (danger count) | "icon" (square, matches small icon buttons).
 * color/textColor and backgroundColor preserve domain identity colors on pills and tags.
 */
const TONES = {
  info: "bg-bun-info-bg text-bun-info",
  success: "bg-bun-success-bg text-bun-success",
  warning: "bg-bun-warning-bg text-bun-warning",
  danger: "bg-bun-danger-bg text-bun-danger",
  special: "bg-bun-special-bg text-bun-special",
  neutral: "bg-bun-mist-deep text-bun-slate",
  yellow: "bg-bun-stage-yellow-bg text-bun-stage-yellow-text",
  orange: "bg-bun-stage-orange-bg text-bun-stage-orange-text",
  "orange-strong": "bg-bun-stage-orange-border text-bun-stage-orange-text",
  purple: "bg-bun-stage-purple-bg text-bun-stage-purple-text",
  "purple-strong": "bg-bun-stage-purple-border text-bun-stage-purple-text",
};

export const MEETING_TYPE_TONES = {
  "check-in": "info",
  planning: "danger",
  prospect: "success",
  trading: "special",
  onboarding: "warning",
};

export default function Badge({ as: Component = "span", tone = "neutral", variant = "pill", size = "sm", dotShape = "circle", month, day, color, textColor, backgroundColor, onRemove, removeLabel, className, children, ...props }) {
  if (variant === 'dot') {
    return <Component {...props} className={cn('inline-flex min-w-0 items-center gap-2 font-instrument', className)}>
      <span aria-hidden="true" className={cn('h-2 w-2 shrink-0', dotShape === 'square' ? 'rounded-[2px]' : 'rounded-full')} style={{ backgroundColor: color }} />
      {children}
    </Component>;
  }
  if (variant === 'answer') {
    return <Component {...props} className={cn(
      'inline-flex max-w-full items-start gap-2 whitespace-pre-wrap break-words rounded-bun-sm px-3 py-2 font-instrument text-bun-body font-strong leading-relaxed',
      TONES[tone] || TONES.neutral,
      className
    )}>{children}</Component>;
  }
  if (variant === 'date') {
    return <Component {...props} className={cn('inline-flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-bun bg-bun-mist-deep font-instrument text-bun-slate', className)}>
      <span className="text-bun-label font-semibold uppercase leading-none">{month}</span>
      <span className="mt-1 text-bun-body font-semibold leading-none">{day}</span>
    </Component>;
  }
  if (variant === 'entity') {
    return <Component {...props} className={cn('inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-bun-line bg-bun-surface py-1 pl-1 pr-3 font-instrument text-bun-body font-strong text-bun-text', className)}>{children}</Component>;
  }
  if (variant === 'tag') {
    return <Component {...props} className={cn('inline-flex items-center', onRemove && 'min-h-11 pr-1 sm:min-h-0 sm:pr-0', className)}>
      <span style={{ color: textColor || color, backgroundColor }} className={cn(
        'relative inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-instrument text-bun-meta font-strong',
        onRemove && 'pr-8 sm:pr-2'
      )}>
        <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        <span className="max-w-[150px] truncate">{children}</span>
        {onRemove && <button type="button" aria-label={removeLabel} onClick={event => { event.stopPropagation(); onRemove(); }}
          className="absolute -right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 shrink-0 items-center justify-center rounded-full hover:opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue sm:static sm:h-4 sm:w-4 sm:translate-y-0">
          <XIcon className="h-3 w-3" />
        </button>}
      </span>
    </Component>;
  }
  if (variant === "outline") {
    return <Component {...props} className={cn(
      "inline-flex min-h-11 items-center gap-2 rounded-bun border border-bun-line bg-bun-surface px-3 text-bun-body font-instrument font-book text-bun-slate sm:min-h-0 sm:py-[7px]",
      className
    )}>{children}</Component>;
  }
  if (variant === "new") {
    return (
      <Component
        className={cn(
          "inline-flex items-center font-instrument text-bun-label normal-case tracking-normal font-semibold",
          "px-2 py-[2px] rounded-[6px] bg-bun-ink text-bun-on-ink",
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
  if (variant === "count") {
    return (
      <Component
        className={cn(
          "inline-flex items-center justify-center font-instrument text-bun-label normal-case tracking-normal font-semibold",
          "px-2 py-[2px] rounded-full",
          TONES[tone] || TONES.danger,
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
  return (
    <Component
      className={cn(
        "inline-flex items-center gap-1 font-instrument text-bun-label normal-case tracking-normal font-semibold",
        "px-[9px] py-[3px] rounded-full whitespace-nowrap",
        TONES[tone],
        variant === "icon" && "h-11 w-11 shrink-0 justify-center rounded-bun-sm p-0",
        variant === "icon" && (size === "lg" ? "sm:h-10 sm:w-10 [&>svg]:h-5 [&>svg]:w-5" : "sm:h-[26px] sm:w-[26px]"),
        className
      )}
      style={{ color: textColor || color, backgroundColor }}
      {...props}
    >
      {children}
    </Component>
  );
}

/** Convenience wrapper: <MeetingBadge type="planning" /> renders the agreed tone. */
export function MeetingBadge({ type, className, children }) {
  const key = String(type || "").toLowerCase();
  return (
    <Badge tone={MEETING_TYPE_TONES[key] || "neutral"} className={className}>
      {children || type}
    </Badge>
  );
}
